import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { supabase } from './useAuth';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type UploadState = 'idle' | 'selected' | 'uploading' | 'processing' | 'complete' | 'error';
export type ProcessingStage = 'quick_scan' | 'chunking' | 'parallel_processing' | 'synthesizing' | null;

export interface AnalysisResult {
  filename: string;
  genre: string | null;
  mood: string | null;
  tempo: number | null;
  duration: number | null;
  lyrics: string | null;
  summary: Record<string, unknown> | null;
  visual_style: Record<string, unknown> | null;
}

interface UseTrackUploadResult {
  state: UploadState;
  file: File | null;
  progress: number;
  error: string | null;
  trackContextId: string | null;
  processingStage: ProcessingStage;
  elapsedSeconds: number;
  estimatedSeconds: number | null;
  analysisResult: AnalysisResult | null;
  selectFile: (file: File) => void;
  startUpload: () => Promise<void>;
  cancelUpload: () => void;
  reset: (force?: boolean) => void;
}

/**
 * Estimate total processing time based on file size.
 */
function estimateProcessingTime(fileSizeBytes: number): number {
  const fileSizeMB = fileSizeBytes / (1024 * 1024);
  const estimatedDurationSeconds = fileSizeMB * 64;
  const numChunks = Math.min(20, Math.ceil(estimatedDurationSeconds / 30));
  const uploadTime = Math.max(3, fileSizeMB * 2);
  const batchCount = Math.ceil(numChunks / 8);
  const processingTime = batchCount * 8;
  const synthesisTime = 15;
  const networkOverhead = 10;
  return Math.ceil(uploadTime + processingTime + synthesisTime + networkOverhead);
}

// Track mount ID globally to detect remounts
let mountCounter = 0;

export function useTrackUpload(personaId: string): UseTrackUploadResult {
  const [state, setState] = useState<UploadState>(() => {
    // This only runs on ACTUAL mount, not re-renders
    mountCounter++;
    console.log(`[Upload] ACTUAL MOUNT #${mountCounter} - initializing with idle state`);
    return 'idle';
  });
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [trackContextId, setTrackContextId] = useState<string | null>(null);
  const [processingStage, setProcessingStage] = useState<ProcessingStage>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [estimatedSeconds, setEstimatedSeconds] = useState<number | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCancelledRef = useRef(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const stateRef = useRef<UploadState>(state);

  // Keep stateRef in sync with state - this allows reset() to see current state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Timer effect - counts seconds while uploading or processing
  useEffect(() => {
    if (state === 'uploading' || state === 'processing') {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }
      timerRef.current = window.setInterval(() => {
        if (startTimeRef.current) {
          setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 100);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (state === 'idle' || state === 'selected') {
        startTimeRef.current = null;
        setElapsedSeconds(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [state]);

  const cleanup = useCallback(() => {
    console.log('[Upload] Cleanup called');
    // Unsubscribe from realtime channel
    if (channelRef.current) {
      console.log('[Upload] Unsubscribing from realtime channel');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const cancelUpload = useCallback(() => {
    // NEVER cancel from 'complete' state - dancing octopus must stay visible
    const currentState = stateRef.current;
    if (currentState === 'complete') {
      console.log('[Upload] BLOCKED cancelUpload - dancing octopus is showing');
      return;
    }

    console.log('[Upload] Cancelling upload');
    isCancelledRef.current = true;
    cleanup();
    setState('idle');
    setFile(null);
    setProgress(0);
    setError(null);
    setTrackContextId(null);
    setProcessingStage(null);
    setEstimatedSeconds(null);
    setAnalysisResult(null);
    startTimeRef.current = null;
    setElapsedSeconds(0);
  }, [cleanup]);

  const selectFile = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setState('selected');
    setError(null);
    setProgress(0);
    setTrackContextId(null);
    setEstimatedSeconds(estimateProcessingTime(selectedFile.size));
  }, []);

  /**
   * Handle completion from realtime subscription
   */
  const handleTrackComplete = useCallback((data: {
    filename?: string;
    primary_genre?: string;
    primary_mood?: string;
    tempo_bpm?: number;
    duration_seconds?: number;
    context_data?: Record<string, unknown>;
  }) => {
    console.log('[Upload] handleTrackComplete called with:', {
      filename: data.filename,
      genre: data.primary_genre,
      mood: data.primary_mood,
      hasContextData: !!data.context_data,
      contextDataKeys: data.context_data ? Object.keys(data.context_data) : [],
    });

    cleanup();
    setProcessingStage(null);

    // Build analysis result from track data
    const contextData = data.context_data || {};
    const transcription = (contextData as { transcription?: { text?: string } }).transcription || {};
    const aiAnalysis = (contextData as { ai_analysis?: Record<string, unknown> }).ai_analysis || {};

    console.log('[Upload] Building analysis result:', {
      hasTranscription: !!transcription.text,
      transcriptionLength: transcription.text?.length || 0,
      hasAiAnalysis: !!aiAnalysis,
      aiAnalysisKeys: Object.keys(aiAnalysis),
    });

    setAnalysisResult({
      filename: data.filename || 'unknown',
      genre: data.primary_genre || null,
      mood: data.primary_mood || null,
      tempo: data.tempo_bpm || null,
      duration: data.duration_seconds || null,
      lyrics: transcription.text || null,
      summary: null,
      visual_style: null,
    });

    console.log('[Upload] Setting state to complete');
    setState('complete');
  }, [cleanup]);

  /**
   * Subscribe to realtime updates for a track context using Supabase Realtime.
   * Event-driven architecture - no polling.
   */
  const subscribeToTrackUpdates = useCallback((contextId: string) => {
    console.log(`[Upload] Subscribing to realtime updates for: ${contextId}`);

    // Create a channel for this specific track
    const channel = supabase
      .channel(`track-${contextId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'track_contexts',
          filter: `id=eq.${contextId}`,
        },
        async (payload) => {
          if (isCancelledRef.current) {
            console.log('[Upload] Update received but cancelled, ignoring');
            return;
          }

          const newData = payload.new as {
            processing_status?: string;
            processing_stage?: string;
            processing_error?: string;
            analyzed_at?: string;
            primary_genre?: string;
            primary_mood?: string;
            tempo_bpm?: number;
            duration_seconds?: number;
            context_data?: Record<string, unknown>;
            filename?: string;
          };

          console.log(`[Upload] Realtime update:`, {
            status: newData.processing_status,
            stage: newData.processing_stage,
          });

          // Update processing stage
          if (newData.processing_stage) {
            setProcessingStage(newData.processing_stage as ProcessingStage);
          }

          // Handle completion
          if (newData.processing_status === 'complete') {
            console.log('[Upload] Analysis complete via realtime! Fetching full track data...');
            // Fetch full track data since realtime payload may not have full context_data
            try {
              const trackResponse = await api.getTrack(contextId);
              console.log('[Upload] Full track fetch response:', {
                hasData: !!trackResponse.data,
                error: trackResponse.error,
                hasContextData: !!trackResponse.data?.context_data,
                contextDataKeys: trackResponse.data?.context_data ? Object.keys(trackResponse.data.context_data) : [],
              });
              if (trackResponse.data) {
                handleTrackComplete({
                  filename: trackResponse.data.filename || undefined,
                  primary_genre: trackResponse.data.primary_genre || undefined,
                  primary_mood: trackResponse.data.primary_mood || undefined,
                  tempo_bpm: trackResponse.data.tempo_bpm || undefined,
                  duration_seconds: trackResponse.data.duration_seconds || undefined,
                  context_data: trackResponse.data.context_data as Record<string, unknown>,
                });
              } else {
                console.error('[Upload] Failed to fetch full track data:', trackResponse.error);
                // Fall back to realtime data
                handleTrackComplete(newData);
              }
            } catch (err) {
              console.error('[Upload] Error fetching full track:', err);
              // Fall back to realtime data
              handleTrackComplete(newData);
            }
          }

          // Handle failure
          if (newData.processing_status === 'failed') {
            console.log('[Upload] Analysis failed via realtime:', newData.processing_error);
            cleanup();
            setProcessingStage(null);
            setState('error');
            setError(newData.processing_error || 'Analysis failed');
          }
        }
      )
      .subscribe((status) => {
        console.log(`[Upload] Realtime subscription status: ${status}`);
      });

    channelRef.current = channel;
  }, [cleanup, handleTrackComplete]);

  const startUpload = useCallback(async () => {
    if (!file) {
      setError('No file selected');
      return;
    }

    // Reset flags and set up abort controller
    isCancelledRef.current = false;
    abortControllerRef.current = new AbortController();

    const fileSizeMB = file.size / (1024 * 1024);
    console.log(`[Upload] File size: ${fileSizeMB.toFixed(1)}MB`);

    try {
      setState('uploading');
      setProgress(0);
      setError(null);

      // Step 1: Get presigned URL
      const presignResponse = await api.getPresignedUrl({
        filename: file.name,
        content_type: file.type || 'audio/mpeg',
        persona_id: personaId,
        upload_type: 'track',
      });

      if (isCancelledRef.current) return;

      if (presignResponse.error || !presignResponse.data) {
        throw new Error(presignResponse.error || 'Failed to get upload URL');
      }

      const { key, upload_url } = presignResponse.data;
      setProgress(10);

      // Step 2: Upload directly to R2
      const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
      const { data: { session } } = await supabase.auth.getSession();
      const uploadResponse = await fetch(`${API_BASE}${upload_url}`, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'audio/mpeg',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (isCancelledRef.current) return;

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      setProgress(60);

      // Step 3: Complete upload (creates DB record and triggers analysis)
      const completeResponse = await api.completeUpload({
        key,
        persona_id: personaId,
        upload_type: 'track',
      });

      if (isCancelledRef.current) return;

      if (completeResponse.error || !completeResponse.data) {
        throw new Error(completeResponse.error || 'Failed to complete upload');
      }

      const record = completeResponse.data.record as { id: string };
      setTrackContextId(record.id);
      setProgress(100);

      // Step 4: Switch to processing state and subscribe to realtime updates
      setState('processing');
      subscribeToTrackUpdates(record.id);

    } catch (err) {
      if (isCancelledRef.current) return;
      cleanup();
      setState('error');
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      // Don't show abort errors as user-facing errors
      if (errorMessage !== 'The user aborted a request.') {
        setError(errorMessage);
      }
    }
  }, [file, personaId, subscribeToTrackUpdates, cleanup]);

  const reset = useCallback((force: boolean = false) => {
    // Use ref to get CURRENT state (not stale closure value)
    const currentState = stateRef.current;

    console.log('[Upload] Reset called', { force, currentState });

    // NEVER reset from 'complete' state unless forced - this is the dancing octopus screen
    // User MUST explicitly click "Upload Another" to reset
    if (currentState === 'complete' && !force) {
      console.log('[Upload] BLOCKED - Dancing octopus is showing, ignoring reset');
      return;
    }

    console.log('[Upload] Performing reset - returning to idle state');
    isCancelledRef.current = false;
    cleanup();
    setState('idle');
    setFile(null);
    setProgress(0);
    setError(null);
    setTrackContextId(null);
    setProcessingStage(null);
    setEstimatedSeconds(null);
    setAnalysisResult(null);
  }, [cleanup]);

  return {
    state,
    file,
    progress,
    error,
    trackContextId,
    processingStage,
    elapsedSeconds,
    estimatedSeconds,
    analysisResult,
    selectFile,
    startUpload,
    cancelUpload,
    reset,
  };
}
